import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Send,
  DollarSign,
  AlertCircle,
  Calendar,
  FileText,
  Briefcase,
  RefreshCw,
} from 'lucide-react';

import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';
import SourcingRfpService from '../../services/sourcingRFPService';

const AddResponse = () => {
  const { rfpId } = useParams();
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rfp, setRfp] = useState(null);

  const [formData, setFormData] = useState({
    lineItems: [],
    notes: ''
  });

  useEffect(() => {
    if (rfpId) fetchInitialData();
  }, [rfpId]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const res = await SourcingRfpService.getRfpById(rfpId);

      // FIX: Handle the nested 'data' object from the API response
      const rfpData = res.data.data || res.data;
      console.log("Debug RFP Data: ", rfpData);
      setRfp(rfpData);

      // FIX: Ensure we are mapping the correct array
      // Mapping the data safely
      const initialLines = (rfpData.lineItems || []).map(item => ({
        lineItemId: item._id,
        // 1. Keep the description logic (Safe)
        description: item.description || item.itemDescription,
        // 2. Keep the requirement text (Safe)
        requirement: item.requirement,
        // 3. ADD 'qty' so we can display it (This is new, but safe!)
        qty: item.qty || 1,
        price: 0,
        notes: ''
      }));

      setFormData({
        lineItems: initialLines,
        notes: ''
      });
    } catch (err) {
      console.error(err);
      showError('Failed to load RFP requirements');
    } finally {
      setLoading(false);
    }
  };

  const handlePriceChange = (index, value) => {
    const updatedLines = [...formData.lineItems];
    updatedLines[index].price = parseFloat(value) || 0;
    setFormData({ ...formData, lineItems: updatedLines });
  };

  const handleNoteChange = (index, value) => {
    const updatedLines = [...formData.lineItems];
    updatedLines[index].notes = value;
    setFormData({ ...formData, lineItems: updatedLines });
  };

  const calculateTotal = () => {
    return formData.lineItems.reduce((sum, item) => sum + item.price, 0);
  };

  const handleSubmit = async (status = 'SUBMITTED') => {
    setSubmitting(true);
    try {
      const payload = {
        lineItems: formData.lineItems,
        notes: formData.notes,
        attachments: [],
        status: status
      };

      await SourcingRfpService.submitResponse(rfpId, payload);

      success(status === 'SUBMITTED' ? 'Proposal submitted!' : 'Draft saved.');
      // Optional: Delay navigation slightly so user sees the success message
      setTimeout(() => navigate('/sourcing/responses'), 1000);
    } catch (err) {
      showError(err.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const headerActions = [
    {
      label: 'Save Draft',
      icon: <Save size={18} />,
      variant: 'outline',
      onClick: () => handleSubmit('DRAFT'),
      disabled: submitting,
    },
    {
      label: submitting ? 'Sending...' : 'Submit Proposal',
      icon: <Send size={18} />,
      variant: 'primary',
      onClick: () => handleSubmit('SUBMITTED'),
      disabled: submitting,
    },
  ];

  if (loading) return (
    <div className="flex justify-center items-center h-screen text-gray-500">
      <RefreshCw className="animate-spin mr-2" /> Loading RFP details...
    </div>
  );

  return (
    <>
      <PageBreadcrumb />

      <PageHeader
        title="Submit Proposal"
        description={`Responding to: ${rfp?.title || rfpId}`}
        onBack={() => navigate(-1)}
        actions={[
          {
            label: 'Save Draft',
            icon: <Save size={18} />,
            variant: 'outline',
            onClick: () => handleSubmit('DRAFT'),
            disabled: submitting,
          },
          {
            label: submitting ? 'Sending...' : 'Submit Proposal',
            icon: submitting ? <RefreshCw className="animate-spin" size={18} /> : <Send size={18} />,
            variant: 'primary',
            onClick: () => handleSubmit('SUBMITTED'),
            disabled: submitting,
          },
        ]}
      />

      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* --- RFP DETAILS CARD (READ ONLY) --- */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
          <div className="grid md:grid-cols-3 gap-6">
            {/* Column 1: Title & Context */}
            <div className="md:col-span-2 space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5 mb-1">
                  <Briefcase size={14} /> RFP Title
                </label>
                <div className="text-lg font-medium text-gray-900 bg-gray-50 p-3 rounded-lg border border-gray-100">
                  {rfp?.title || "Untitled RFP"}
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5 mb-1">
                  <FileText size={14} /> Scope of Work
                </label>
                <div className="text-sm text-gray-700 bg-gray-50 p-4 rounded-lg border border-gray-100 min-h-[80px] leading-relaxed">
                  {rfp?.scopeOfWork || rfp?.scope || "No scope provided."}
                </div>
              </div>
            </div>

            {/* Column 2: Deadlines & Status */}
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5 mb-1">
                  <Calendar size={14} /> Response Deadline
                </label>
                <div className="text-sm font-medium text-red-600 bg-red-50 p-3 rounded-lg border border-red-100">
                  {rfp?.responseDeadline ? new Date(rfp.responseDeadline).toLocaleDateString(undefined, {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : "No Deadline Set"}
                </div>
              </div>

              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100 text-sm text-blue-800">
                <p className="font-semibold mb-1 flex items-center gap-1">
                   <AlertCircle size={14} /> Instructions:
                </p>
                <p className="opacity-80">
                  Please fill out the unit price for each line item below. You may add notes for deviations or bulk discount details.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* --- PRICING TABLE --- */}
        {formData.lineItems.length === 0 ? (
          <div className="bg-yellow-50 border border-yellow-200 p-8 rounded-xl text-center">
            <AlertCircle className="mx-auto text-yellow-500 mb-2" />
            <p className="text-yellow-700 font-medium">This RFP has no line items yet.</p>
            <p className="text-sm text-yellow-600">Please contact the sourcing manager.</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
              <h3 className="font-semibold text-gray-800">Bid Line Items</h3>
              <span className="text-xs bg-gray-200 px-2 py-1 rounded text-gray-600">{formData.lineItems.length} Items</span>
            </div>

            <table className="w-full text-left">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="p-4 text-xs font-bold text-gray-500 uppercase w-1/3">Item / Requirement</th>
                  <th className="p-4 text-xs font-bold text-gray-500 uppercase w-48">Your Unit Price</th>
                  <th className="p-4 text-xs font-bold text-gray-500 uppercase">Vendor Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {formData.lineItems.map((item, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 transition-colors">
                    <td className="p-4 align-top">
                      <p className="font-medium text-gray-900">{item.description}</p>
                      <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                        {item.qty && (
                          <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs font-semibold">
                            Qty: {item.qty}
                          </span>
                        )}
                        <span>{item.requirement}</span>
                      </div>
                    </td>
                    <td className="p-4 align-top">
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                        <input
                          type="number"
                          min="0"
                          className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                          placeholder="0.00"
                          value={item.price}
                          onChange={(e) => handlePriceChange(idx, e.target.value)}
                        />
                      </div>
                    </td>
                    <td className="p-4 align-top">
                      <textarea
                        rows="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-none overflow-hidden"
                        placeholder="Add comments..."
                        value={item.notes}
                        onChange={(e) => handleNoteChange(idx, e.target.value)}
                        onFocus={(e) => e.target.rows = 3}
                        onBlur={(e) => e.target.rows = 1}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-blue-50/50 border-t border-gray-200">
                <tr>
                  <td className="p-4 text-sm text-gray-500">Totals calculated automatically</td>
                  <td className="p-4 text-right font-bold text-gray-700">Total Bid:</td>
                  <td className="p-4">
                    <span className="text-xl font-bold text-blue-700">
                      ${calculateTotal().toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default AddResponse;